import { Icon, IconProps } from '../ui/icon';
import { ReactComponent as EmpowerGPTLogo } from 'src/assets/tailwind/empowergpt-logo.svg';
import { ReactComponent as Tick } from 'src/assets/tailwind/check.svg';
import { ReactComponent as GDPR_ICON } from 'src/assets/tailwind/gdpr-compliant.svg';
import { ReactComponent as SOC } from 'src/assets/tailwind/soc.svg';
import { ReactComponent as Hosting } from 'src/assets/tailwind/hosting.svg';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

const IconTab = ({
  label,
  icon
}: {
  icon: Pick<IconProps, 'icon'>['icon'];
  label: string;
}) => (
  <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:text-sm tw:w-[118.67px] tw:min-w-0 tw:shrink-0 tw:h-24">
    <div className="tw:w-[70px] tw:h-[70px]">
      <Icon icon={icon} size={70} />
    </div>
    <div className="tw:font-medium tw:tracking-normal tw:leading-3 tw:text-text-primary tw:h-6 tw:flex tw:items-center tw:text-lg">
      {label}
    </div>
  </div>
);

const Paragraph = ({ text }: { text: string }) => (
  <p className="tw:font-normal tw:tracking-normal tw:text-text-secondary tw:text-md tw:leading-relaxed">
    {text}
  </p>
);

const Heading3 = ({ text }: { text: string }) => (
  <h3 className="tw:font-semibold tw:mb-5 tw:text-xl tw:tracking-normal tw:text-text-primary">
    {text}
  </h3>
);

export const InfoSection = () => {
  const { t } = useTranslation();
  return (
    <div className="tw:min-w-0 tw:px-3 tw:py-3 tw:flex tw:flex-col tw:justify-between tw:bg-white tw:w-full">
      <div className="tw:flex tw:flex-col tw:gap-[35px] tw:top-6 tw:bottom-6 tw:py-6 tw:px-4 tw:sm:tw:px-6 tw:min-w-0">
        {/* Logo */}
        <div className="tw:flex tw:items-center tw:min-w-0">
          <Icon icon={EmpowerGPTLogo} width={140} height={40} />
        </div>

        {/* Trial Section */}
        <div className="tw:flex tw:flex-col tw:gap-[43px] tw:min-w-0">
          <div>
            <h1 className="tw:text-2xl tw:sm:tw:text-3xl tw:text-text-primary tw:font-semibold tw:leading-tight tw:tracking-normal">
              {t(T.trialHeading)}
            </h1>
            <div className="tw:w-full tw:max-w-[480px] tw:min-h-[70px]">
              <Paragraph text={t(T.trialDescription)} />
              <Paragraph text={t(T.trustedEnterprise)} />
            </div>
          </div>

          <div>
            <Heading3 text={t(T.freeTrial7DayHeading)} />
            <ul className="tw:space-y-3 tw:text-gray-700">
              {[
                t(T.noCreditCard),
                t(T.secureKnowledge),
                t(T.teamReady),
                t(T.anyDocumentUpload),
                t(T.easyUpgrade),
                t(T.teamReadyEnvironment)
              ].map((item) => (
                <li
                  key={item}
                  className="tw:flex tw:items-center tw:gap-3 tw:text-md tw:text-text-primary tw:tracking-normal tw:leading-5"
                >
                  <span className="tw:text-green-500">
                    <Icon icon={Tick} color="green" width={16} height={16} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Compliance */}
        <div className="tw:gap-3 tw:flex tw:flex-col">
          <Heading3 text={t(T.compliance)} />
          <div className="tw:w-full tw:max-w-[480px] tw:min-h-36 tw:rounded-xl tw:flex tw:flex-wrap tw:justify-between tw:gap-3 tw:border tw:border-border-primary tw:items-center tw:p-3">
            <IconTab icon={GDPR_ICON} label="GDPR" />
            <IconTab icon={SOC} label="SOC" />
            <IconTab icon={Hosting} label="EU Hosting" />
          </div>
        </div>
      </div>
    </div>
  );
};
